import React from 'react';
import ApiAutocomplete, { ApiAutocompleteProps, ApiAutocompleteOption } from './ApiAutocomplete';

// ── Types (preserved exactly) ─────────────────────────────────────────────────
export interface Country extends ApiAutocompleteOption {
  id: number;
  name: string;
  iso2: string;
  iso3: string;
  phone_code: string;
  emoji: string;
  currency: string;
  currency_symbol: string;
  region: string;
  subregion: string;
}

export interface State extends ApiAutocompleteOption {
  id: number;
  name: string;
  state_code: string;
  type: string;
}

export interface City extends ApiAutocompleteOption {
  id: number;
  name: string;
}

export interface CountrySelectorProps extends Omit<ApiAutocompleteProps<Country>, 'endpoint' | 'labelKey' | 'valueKey' | 'renderOption'> {
  showFlag?: boolean;
  showCurrency?: boolean;
  showRegion?: boolean;
}

export interface StateSelectorProps extends Omit<ApiAutocompleteProps<State>, 'endpoint' | 'labelKey' | 'valueKey' | 'additionalParams'> {
  countryId?: number;
}

export interface CitySelectorProps extends Omit<ApiAutocompleteProps<City>, 'endpoint' | 'labelKey' | 'valueKey' | 'additionalParams'> {
  stateId?: number;
  countryId?: number;
}

// ── CountrySelector ───────────────────────────────────────────────────────────
export const CountrySelector: React.FC<CountrySelectorProps> = ({
  showFlag = true,
  showCurrency = false,
  showRegion = false,
  searchParamName = 'name',
  ...props
}) => {
  const renderCountryOption = (optionProps: any, option: Country) => {
    return (
      <button
        key={option.id}
        type="button"
        {...optionProps}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', textAlign: 'left',
          padding: '8px 14px', border: 'none', cursor: 'pointer',
          background: 'transparent', color: 'var(--ink)',
        }}
      >
        {showFlag && (
          <span style={{ fontSize: 18, lineHeight: 1 }}>{option.emoji || '🏳️'}</span>
        )}
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13 }}>{option.name}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {option.iso2}
            {showCurrency && option.currency && ` · ${option.currency} ${option.currency_symbol}`}
            {showRegion && option.region && ` · ${option.region}`}
          </span>
        </span>
      </button>
    );
  };

  const renderCountryTags = (value: Country[], getTagProps: any) => {
    return value.map((option, index) => {
      const { key, onDelete } = getTagProps({ index });
      return (
        <span
          key={key}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 4,
            background: 'rgba(59,130,246,0.1)', color: 'var(--brand-blue)',
            fontSize: 12, border: '1px solid rgba(59,130,246,0.2)',
          }}
        >
          {showFlag && <span>{option.emoji || '🏳️'}</span>}
          <span>{option.name}</span>
          <button
            type="button"
            onClick={onDelete}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', lineHeight: 1 }}
          >×</button>
        </span>
      );
    });
  };

  return (
    <ApiAutocomplete<Country>
      endpoint="/countries"
      labelKey="name"
      valueKey="id"
      searchParamName={searchParamName}
      renderOption={renderCountryOption}
      renderTags={props.multiple ? renderCountryTags : undefined}
      placeholder="Search countries..."
      minSearchLength={1}
      debounceMs={500}
      {...props}
    />
  );
};

// ── StateSelector ─────────────────────────────────────────────────────────────
export const StateSelector: React.FC<StateSelectorProps> = ({
  countryId,
  searchParamName = 'name',
  ...props
}) => {
  const renderStateOption = (optionProps: any, option: State) => {
    return (
      <button
        key={option.id}
        type="button"
        {...optionProps}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', textAlign: 'left',
          padding: '8px 14px', border: 'none', cursor: 'pointer',
          background: 'transparent', color: 'var(--ink)',
        }}
      >
        <span style={{ fontSize: 14, color: 'var(--ink-3)' }}>📍</span>
        <span>
          <span style={{ display: 'block', fontSize: 13 }}>{option.name}</span>
          {option.state_code && (
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {option.state_code} · {option.type || 'State'}
            </span>
          )}
        </span>
      </button>
    );
  };

  const helperText = props.helperText ?? (!countryId ? 'Please select a country first' : undefined);
  const placeholder = !countryId ? 'Select a country first...' : 'Search states/provinces...';

  return (
    <ApiAutocomplete<State>
      endpoint="/states"
      labelKey="name"
      valueKey="id"
      searchParamName={searchParamName}
      additionalParams={countryId ? { country_id: countryId } : {}}
      renderOption={renderStateOption}
      placeholder={placeholder}
      disabled={!countryId || props.disabled}
      helperText={helperText}
      minSearchLength={1}
      debounceMs={500}
      {...props}
    />
  );
};

// ── CitySelector ──────────────────────────────────────────────────────────────
export const CitySelector: React.FC<CitySelectorProps> = ({
  stateId,
  countryId,
  searchParamName = 'name',
  ...props
}) => {
  const renderCityOption = (optionProps: any, option: City) => {
    return (
      <button
        key={option.id}
        type="button"
        {...optionProps}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', textAlign: 'left',
          padding: '8px 14px', border: 'none', cursor: 'pointer',
          background: 'transparent', color: 'var(--ink)',
        }}
      >
        <span style={{ fontSize: 14, color: 'var(--ink-3)' }}>🏙️</span>
        <span style={{ fontSize: 13 }}>{option.name}</span>
      </button>
    );
  };

  const getAdditionalParams = () => {
    if (stateId) return { state_id: stateId };
    if (countryId) return { country_id: countryId };
    return {};
  };

  const isDisabled = (!stateId && !countryId) || props.disabled;
  const helperText = props.helperText ?? (!stateId && !countryId ? 'Please select a state/province or country first' : undefined);
  const placeholder = !stateId && !countryId ? 'Select a state/province or country first...' : 'Search cities...';

  return (
    <ApiAutocomplete<City>
      endpoint="/cities"
      labelKey="name"
      valueKey="id"
      searchParamName={searchParamName}
      additionalParams={getAdditionalParams()}
      renderOption={renderCityOption}
      placeholder={placeholder}
      disabled={isDisabled}
      helperText={helperText}
      minSearchLength={1}
      debounceMs={500}
      {...props}
    />
  );
};
